/**
 * gameAudio/music.ts — background music bus: light recorded piano/lullaby/lofi
 * playlists on a dedicated low-gain channel that auto-advances on track end.
 * PR-16 split from lib/gameAudio.ts.
 *
 * Style picker: the kid can choose piano (default), lullaby, or lofi from the
 * AppShell music menu. setMusicStyle persists in localStorage and dispatches
 * a 'xiaopeiban:music-style' window event so the picker re-renders. Each
 * style maps to a small fixed playlist of /sounds/music/<style>_<n>.mp3
 * files dropped in by tools/fetch_music.py. If a style's files aren't yet on
 * disk we silently fall back to piano so the layer never goes mute by
 * accident.
 *
 * Mute interplay: getMusicEnabled() controls only the music channel — scene
 * ambience + pet voices keep playing regardless. The master setAudioEnabled()
 * mute does silence music too via the registered audio-off hook below.
 *
 * Concurrency safety: stopBackgroundMusic() invalidates the run token AND
 * tears down musicGain via _context.replaceMusicGain() so any in-flight
 * decode + start cannot ring through after a style change. canUseMusicRun()
 * is the guard each await-point checks against the current token + style.
 *
 * 调用关系：
 *   - AppShell + MeSettingsPage + AppShellV2 wire the music picker UI to
 *     get/setMusicStyle, get/setMusicEnabled, getMusicStyleOptions().
 *   - _context.unlockAudio() / setAudioEnabled() drive the registered
 *     audio-off + audio-unlock hooks at the bottom of this file.
 *   - Translations come from frontend/src/i18n via t('audio.style.*').
 *
 * 关键约束：
 *   - Don't call startBackgroundMusic() before audio is unlocked — the
 *     promise chain works in suspended state but the buffer source can't
 *     produce audible output until the first user gesture.
 *   - Track files must follow the /sounds/music/<style>_<n>.mp3 convention;
 *     the playlist arrays below are the only source of truth, no
 *     directory-listing fallback.
 */

import { t } from '../../i18n';
import {
  ensureAudio,
  getAudioEnabled,
  getCtx,
  getMusicGain,
  registerOnAudioOff,
  registerOnAudioUnlock,
  replaceMusicGain,
} from './_context';

const MUSIC_STYLE_KEY = 'xiaopeiban_music_style';
const MUSIC_ENABLED_KEY = 'xiaopeiban_music_enabled';

// Long fade-in so the piano breathes; instant stop fade (0s) on manual
// changes so style-swap doesn't double up two tracks.
const MUSIC_FADE = 1.5;
const MUSIC_STOP_FADE = 0;

// Three selectable styles. One is pure piano (the original); the others give
// the child a choice. If a style's files aren't yet on disk the layer goes
// silent for that style — runtime degrades gracefully.
export type MusicStyle = 'piano' | 'lullaby' | 'lofi';

export interface MusicStyleOption {
  /** localStorage value + lookup key for MUSIC_TRACKS_BY_STYLE. */
  value: MusicStyle;
  /** Translated user-facing label (already i18n'd, do not re-translate). */
  label: string;
  /** Emoji glyph shown in the picker; chosen for visual distinctness. */
  emoji: string;
  /** Short helper text under the label (e.g. "calm piano"). */
  helper: string;
}

// Lazy: looks up labels via i18n at call time so the picker re-renders in the
// active language (callers in React land subscribe to lang via useLang).
export function getMusicStyleOptions(): MusicStyleOption[] {
  return [
    // PR-4.2: piano now uses 🎵 (a friendlier "music note") with a 背景音乐
    // tooltip handled by AppShell via the music.tooltip.* keys; the lullaby
    // entry shifts to 🌙 so the two are visually distinct in the picker.
    { value: 'piano',   emoji: '🎵', label: t('audio.style.piano.label'),   helper: t('audio.style.piano.helper') },
    { value: 'lullaby', emoji: '🌙', label: t('audio.style.lullaby.label'), helper: t('audio.style.lullaby.helper') },
    { value: 'lofi',    emoji: '🎧', label: t('audio.style.lofi.label'),    helper: t('audio.style.lofi.helper') },
  ];
}

const MUSIC_TRACKS_BY_STYLE: Record<MusicStyle, string[]> = {
  piano: [
    '/sounds/music/piano_1.mp3',
    '/sounds/music/piano_2.mp3',
    '/sounds/music/piano_3.mp3',
    '/sounds/music/piano_4.mp3',
  ],
  lullaby: [
    '/sounds/music/lullaby_1.mp3',
    '/sounds/music/lullaby_2.mp3',
    '/sounds/music/lullaby_3.mp3',
    '/sounds/music/lullaby_4.mp3',
  ],
  lofi: [
    '/sounds/music/lofi_1.mp3',
    '/sounds/music/lofi_2.mp3',
    '/sounds/music/lofi_3.mp3',
    '/sounds/music/lofi_4.mp3',
  ],
};

let currentStyle: MusicStyle = (() => {
  if (typeof window === 'undefined') return 'piano';
  const saved = window.localStorage.getItem(MUSIC_STYLE_KEY);
  return (saved === 'piano' || saved === 'lullaby' || saved === 'lofi') ? saved : 'piano';
})();

function tracksForStyle(style: MusicStyle): string[] {
  return MUSIC_TRACKS_BY_STYLE[style] || MUSIC_TRACKS_BY_STYLE.piano;
}

export function getMusicStyle(): MusicStyle { return currentStyle; }

export function setMusicStyle(style: MusicStyle) {
  if (currentStyle === style) {
    if (getAudioEnabled() && getMusicEnabled()) void resumeAndStartBackgroundMusic();
    return;
  }
  currentStyle = style;
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(MUSIC_STYLE_KEY, style);
    window.dispatchEvent(new CustomEvent('xiaopeiban:music-style', { detail: { style } }));
  }
  // Reset rotation index for the new style and swap track immediately. This
  // invalidates any old async load so a previous style cannot start later.
  musicNextIdx = 0;
  stopBackgroundMusic({ fadeSeconds: MUSIC_STOP_FADE });
  if (getAudioEnabled() && getMusicEnabled()) void resumeAndStartBackgroundMusic();
}

// Music-only on/off — independent from the master audio flag. Scene ambience
// and pet voices keep playing regardless; turning music off only silences the
// piano/lullaby/lofi background channel.
export function getMusicEnabled(): boolean {
  if (typeof window === 'undefined') return true;
  return window.localStorage.getItem(MUSIC_ENABLED_KEY) !== 'off';
}

export function setMusicEnabled(enabled: boolean) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(MUSIC_ENABLED_KEY, enabled ? 'on' : 'off');
  window.dispatchEvent(new CustomEvent('xiaopeiban:music-toggle', { detail: { enabled } }));
  if (!enabled) {
    stopBackgroundMusic({ fadeSeconds: MUSIC_STOP_FADE });
    return;
  }
  if (getAudioEnabled()) void resumeAndStartBackgroundMusic();
}

const musicBufferCache = new Map<string, AudioBuffer>();
const musicLoading = new Map<string, Promise<AudioBuffer | null>>();
const musicFailed = new Set<string>();
type MusicTrack = { source: AudioBufferSourceNode; gain: GainNode; path: string };
let musicCurrent: MusicTrack | null = null;
const musicTracks = new Set<MusicTrack>();
let musicNextIdx = 0;
let musicRunToken = 0;
let musicStarting: { token: number; promise: Promise<void> } | null = null;

function canUseMusicRun(token: number, style: MusicStyle): boolean {
  return token === musicRunToken
    && currentStyle === style
    && getAudioEnabled()
    && getMusicEnabled();
}

async function resumeAndStartBackgroundMusic(): Promise<void> {
  const audio = ensureAudio();
  if (audio?.state === 'suspended') {
    try { await audio.resume(); } catch { /* browser may require a later tap */ }
  }
  await startBackgroundMusic();
}

function disconnectMusicTrack(track: MusicTrack): void {
  try { track.source.disconnect(); } catch { /* already disconnected */ }
  try { track.gain.disconnect(); } catch { /* already disconnected */ }
}

async function loadMusicTrack(path: string): Promise<AudioBuffer | null> {
  if (musicBufferCache.has(path)) return musicBufferCache.get(path) || null;
  if (musicFailed.has(path)) return null;
  const inflight = musicLoading.get(path);
  if (inflight) return inflight;
  const audio = ensureAudio();
  if (!audio) return null;
  const promise = (async () => {
    try {
      const res = await fetch(path);
      if (!res.ok) throw new Error(`status ${res.status}`);
      const arr = await res.arrayBuffer();
      const buf = await audio.decodeAudioData(arr.slice(0));
      musicBufferCache.set(path, buf);
      return buf;
    } catch {
      musicFailed.add(path);
      return null;
    } finally {
      musicLoading.delete(path);
    }
  })();
  musicLoading.set(path, promise);
  return promise;
}

async function startBackgroundMusic(): Promise<void> {
  if (typeof window === 'undefined') return;
  if (!getAudioEnabled()) return;
  if (!getMusicEnabled()) return;
  if (musicCurrent) return;
  const token = musicRunToken;
  const styleAtStart = currentStyle;
  if (musicStarting?.token === token) return musicStarting.promise;

  const promise = startBackgroundMusicRun(token, styleAtStart).finally(() => {
    if (musicStarting?.token === token) musicStarting = null;
  });
  musicStarting = { token, promise };
  return promise;
}

async function startBackgroundMusicRun(token: number, styleAtStart: MusicStyle): Promise<void> {
  if (!canUseMusicRun(token, styleAtStart)) return;
  // Pick the next available track in the rotation, skipping any that fail.
  // Use the currently-selected style's playlist; if every track for that
  // style is missing (404), silently fall back to piano so the child still
  // hears something while we wait for a fetch_music run.
  let buf: AudioBuffer | null = null;
  let path = '';
  const styles: MusicStyle[] = styleAtStart === 'piano' ? ['piano'] : [styleAtStart, 'piano'];
  for (const style of styles) {
    const tracks = tracksForStyle(style);
    for (let i = 0; i < tracks.length; i += 1) {
      if (!canUseMusicRun(token, styleAtStart)) return;
      const idx = (musicNextIdx + i) % tracks.length;
      const candidate = tracks[idx];
      const loaded = await loadMusicTrack(candidate);
      if (!canUseMusicRun(token, styleAtStart)) return;
      if (loaded) {
        buf = loaded;
        path = candidate;
        musicNextIdx = (idx + 1) % tracks.length;
        break;
      }
    }
    if (buf) break;
  }
  if (!buf) return; // no music files on disk yet — silent music channel

  const audio = ensureAudio();
  const music = getMusicGain();
  if (!audio || !music) return;
  if (audio.state === 'suspended') {
    try { await audio.resume(); } catch { /* browser may require a later tap */ }
  }
  if (!canUseMusicRun(token, styleAtStart)) return;
  if (musicCurrent) return;

  const source = audio.createBufferSource();
  source.buffer = buf;
  const gain = audio.createGain();
  const t = audio.currentTime;
  gain.gain.setValueAtTime(0.0001, t);
  gain.gain.linearRampToValueAtTime(1.0, t + MUSIC_FADE);
  source.connect(gain);
  gain.connect(music);
  source.start();

  const track = { source, gain, path };
  musicCurrent = track;
  musicTracks.add(track);

  source.onended = () => {
    musicTracks.delete(track);
    disconnectMusicTrack(track);
    if (musicCurrent !== track) return; // we were stopped/replaced — ignore
    musicCurrent = null;
    if (getAudioEnabled() && getMusicEnabled()) void startBackgroundMusic();
  };
}

function stopBackgroundMusic(options: { fadeSeconds?: number } = {}) {
  musicRunToken += 1;
  musicStarting = null;
  const tracks = [...musicTracks];
  musicTracks.clear();
  musicCurrent = null;
  replaceMusicGain();
  if (!tracks.length) return;
  const audio = getCtx();
  if (!audio) {
    tracks.forEach((track) => {
      try { track.source.stop(); } catch {}
      disconnectMusicTrack(track);
    });
    return;
  }
  const t = audio.currentTime;
  const fadeSeconds = Math.max(0, options.fadeSeconds ?? MUSIC_FADE);
  tracks.forEach((track) => {
    const { source, gain } = track;
    try {
      gain.gain.cancelScheduledValues(t);
      gain.gain.setValueAtTime(0.0001, t);
      if (fadeSeconds > 0) gain.gain.linearRampToValueAtTime(0.0001, t + fadeSeconds);
    } catch {}
    try { source.stop(t + fadeSeconds); } catch {}
    disconnectMusicTrack(track);
  });
}

// Lifecycle hooks: master mute stops music with instant fade so two tracks
// never overlap; unlock event resumes if music is enabled.
registerOnAudioOff(() => {
  stopBackgroundMusic({ fadeSeconds: MUSIC_STOP_FADE });
});
registerOnAudioUnlock(() => {
  if (getMusicEnabled()) void startBackgroundMusic();
});
